import { Button, Space, Table } from "antd";
import { ColumnType } from "antd/es/table";
import useModal from "antd/es/modal/useModal";
import numeral from "numeral";

import { usePagination } from "../../hooks/usePagintation";
import { useProductDetail, useProducts, useDeleteProduct } from "../../hooks/apis/product";

import { ProductDetail } from "./ProductDetail";

type ProductListProps = {
  products: Array<{
    id: number;
    merchantId: number;
    isSales: boolean;
    brand: {
      id: number;
      name: string;
      types: string[];
    };
    type: string;
    modal: {};
    title: string;
    years: number;
    price: number;
    tags: string[];
    color?: string;
    kilometers: number;
    imageUrl: string;
    images: { id: number; isCover: boolean; url: string }[];
    creator: {
      id: number;
      name: {};
      email: string;
    };
  }>;
};

export function ProductList() {
  const pagination = usePagination({
    defaultPageSize: 30,
  });

  const { data: { cars = [], total } = {}, isLoading: isProductsLoading } = useProducts({
    take: pagination.take,
    skip: pagination.skip,
  });
  const { mutateAsync: getProductDetail } = useProductDetail();
  const { mutateAsync: deleteProduct, isLoading: isDeleteLoading } = useDeleteProduct();

  const [modal, contextHolder] = useModal();

  const onEditButtonClick = async (product: ProductListProps["products"][number]) => {
    const respProductDetail = (await getProductDetail({ id: product.id }))?.data.car || {};

    modal.info({
      cancelText: "取消",
      content: (
        <ProductDetail
          product={{
            ...respProductDetail,
            imageUrl: respProductDetail.images.find((item: { isCover: boolean }) => item.isCover).url,
          }}
        />
      ),
      icon: null,
      okText: "關閉",
      title: "商品詳情",
    });
  };

  const onDeleteButtonClick = async (product: ProductListProps["products"][number]) => {
    modal.confirm({
      cancelText: "取消",
      content: "確定要刪除嗎？",
      icon: null,
      okText: "確認",
      title: "異動商品",
      onOk: async () => {
        return deleteProduct(product.id);
      },
    });
  };

  const columns: ColumnType<ProductListProps["products"][number]>[] = [
    { align: "center", dataIndex: "id", title: "ID", width: 50 },
    {
      dataIndex: "isSales",
      title: "狀態",
      width: 50,
      render: (isSales: boolean) => (
        <span className={isSales ? "text-green-500" : "text-red-500"}>{isSales ? "上架" : "下架"}</span>
      ),
    },
    {
      dataIndex: ["brand", "name"],
      title: "車廠",
      width: 120,
    },
    {
      dataIndex: "type",
      title: "車款",
      width: 120,
    },
    {
      dataIndex: "modal",
      title: "車種",
      width: 120,
    },
    {
      dataIndex: "color",
      title: "車色",
      width: 80,
    },
    {
      dataIndex: "kilometers",
      title: "里程",
      width: 120,
      render: (kilometers: number) => numeral(kilometers).format("0,0"),
    },
    {
      dataIndex: "price",
      title: "價錢",
      width: 120,
      render: (price: number) => numeral(price).format("$0,0"),
    },
    {
      title: "操作",
      width: 150,
      render: product => {
        return (
          <Space size="small">
            <Button
              disabled={product.isProtected}
              onClick={() => onEditButtonClick(product)}
              size="small"
              type="primary"
            >
              詳情
            </Button>
            <Button onClick={() => onDeleteButtonClick(product)} size="small" type="default" danger>
              刪除
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" className="w-full">
      <div className="text-2xl font-bold">商品管理</div>

      {contextHolder}

      <Table
        dataSource={cars || []}
        columns={columns}
        loading={isProductsLoading || isDeleteLoading}
        pagination={{ ...pagination, total }}
        scroll={{ x: "max-content" }}
        size="small"
        rowKey="id"
      ></Table>
    </Space>
  );
}
